import { useMemo } from 'react';
import { useFileUploaderOptions } from '@components/FileUploader';
import { DOCUMENT } from '@config/constants';
import { useRemoveAttachmentMutation } from '../graphql/useRemoveAttachmentMutation';

const buildRejectedProps = (document) => {
  if (!document || document.noFiles) return [];
  if (document.status === DOCUMENT.STATUS.APPROVED) return [];

  return document
    .attachments
    .filter((attachment) => attachment.status === DOCUMENT.STATUS.REJECTED)
    .map((attachment) => ({
      id: attachment.id,
      fileName: attachment.fileName,
      message: attachment.reasonForRejection,
    }));
};

const buildUploadedProps = (document) => {
  if (!document || document.noFiles) return [];
  const statuses = [];

  if (document.status === DOCUMENT.STATUS.APPROVED) {
    statuses.push(DOCUMENT.STATUS.APPROVED);
  }
  if (document.status === DOCUMENT.STATUS.PENDING) {
    statuses.push(
      DOCUMENT.STATUS.APPROVED,
      DOCUMENT.STATUS.PENDING,
      DOCUMENT.ANALYST_STATUS.PENDING,
    );
  }

  return document
    .attachments
    .filter((attachment) => statuses.includes(attachment.status))
    .map((attachment) => ({
      id: attachment.id,
      status: attachment.status,
      storageServiceStatus: attachment.storageServiceStatus,
      fileName: attachment.fileName,
    }));
};

export const useController = ({
  control,
  document,
  clearField,
  onUploadError,
  onUploadCompleted,
  onRemoveCompleted,
  filesCount: filesCountProp,
}) => {
  const fileFieldOptions = useFileUploaderOptions({
    control,
    clearField,
    onUploadError,
    onUploadCompleted,
    documentOptions: {
      documentClass: document?.documentClass,
      documentType: document?.documentType,
      documentGrouping: document?.documentGrouping,
    },
  });
  const { isUploading = false } = fileFieldOptions;

  const [
    removeAttachmentMutation,
    { loading: isRemoveLoading },
  ] = useRemoveAttachmentMutation({
    onCompleted: onRemoveCompleted,
  });

  const onRemoveFile = ({ attachmentId }) => {
    removeAttachmentMutation({
      variables: { input: { attachmentId: parseInt(attachmentId, 10) } },
    });
  };

  const rejectedFiles = useMemo(
    () => buildRejectedProps(document),
    [document],
  );
  const uploadedFiles = useMemo(
    () => buildUploadedProps(document),
    [document],
  );

  const filesCount = useMemo(() => {
    if (!document || document.noFiles) return filesCountProp;
    if (document.status === DOCUMENT.STATUS.APPROVED) return 0;
    if (document.status === DOCUMENT.STATUS.PENDING) {
      const pendingFilesCount = document
        .attachments
        .reduce((acc, attachment) => (
          attachment.status === DOCUMENT.ANALYST_STATUS.PENDING ? acc + 1 : acc), 0);
      return filesCountProp - pendingFilesCount;
    }
    return filesCountProp;
  }, [filesCountProp, document]);

  const showRejectedAlert = rejectedFiles.length > 0;

  return {
    filesCount,
    isUploading,
    onRemoveFile,
    rejectedFiles,
    uploadedFiles,
    isRemoveLoading,
    fileFieldOptions,
    showRejectedAlert,
  };
};
